import {
  Navigate,
  RouteObject,
  createHashRouter,
  createMemoryRouter,
  useRouteError,
} from 'react-router-dom';

import App from './AppContainer';
import Login from './Login/LoginContainer';
import MessageView from './MessageView/MessageViewContainer';
import NoPersonGroupSelected from './components/NoPersonGroupSelected';
import { TeamContainer } from './Team';
import { connect } from './rootReducer';
import { getSiteRoutes } from './routes';
import { isCordovaApp } from '@pdcshell/helpers/utils';
import { useIsLoggedIn } from './hooks/useIsLoggedIn';
import { useEffect } from 'react';
import getApi from './getApi';
import { ReactError } from '@pdcplan/shared/error';

const ErrorElement = () => {
  const error = useRouteError();

  useEffect(() => {
    if (error instanceof Error) {
      getApi()
        .log(new ReactError({ type: 'route', message: error.message }))
        .catch(() => {
          //ignore
        });
    }
  }, [error]);

  return null;
};

/**
 * Guard that checks if the user is logged in, and if not, redirects to the login page.
 */
const AuthGuard = ({ children }: { children: JSX.Element }) => {
  const isLoggedIn = useIsLoggedIn();
  return isLoggedIn ? (
    children
  ) : (
    <Navigate to={getSiteRoutes().login} replace />
  );
};

/**
 * The inverse of AuthGuard. If the user is logged in, redirect to the team or teams page.
 */
const MaybeLogin = connect(
  (store) => ({
    currentTeam: store.listViewReducer.currentTeam,
  }),
  () => ({})
)(({ currentTeam }) => {
  const isLoggedIn = useIsLoggedIn();
  return isLoggedIn ? (
    <Navigate
      to={
        currentTeam ? getSiteRoutes().team(currentTeam) : getSiteRoutes().teams
      }
      replace
    />
  ) : (
    <Login />
  );
});

const routes: RouteObject[] = [
  {
    path: '/',
    element: <App />, // App is always rendered, regardless of the route
    errorElement: <ErrorElement />,
    children: [
      {
        path: getSiteRoutes().login,
        element: <MaybeLogin />,
        errorElement: <ErrorElement />,
        index: true,
      },
      {
        path: getSiteRoutes().teams,
        element: (
          <AuthGuard>
            <NoPersonGroupSelected />
          </AuthGuard>
        ),
        errorElement: <ErrorElement />,
        index: true,
      },
      {
        path: getSiteRoutes().team(),
        element: (
          <AuthGuard>
            <TeamContainer />
          </AuthGuard>
        ),
        errorElement: <ErrorElement />,
        index: true,
      },
      {
        path: getSiteRoutes().chat(),
        element: (
          <AuthGuard>
            <MessageView />
          </AuthGuard>
        ),
        errorElement: <ErrorElement />,
        index: true,
      },
      // Matches exactly "/"
      {
        element: <MaybeLogin />,
        errorElement: <ErrorElement />,
        index: true,
      },
    ],
  },
  // 404
  {
    path: '*',
    element: <Navigate to={getSiteRoutes().team()} />,
    errorElement: <ErrorElement />,
    index: true,
  },
];

export const router = isCordovaApp()
  ? createMemoryRouter(routes)
  : createHashRouter(routes);
